import { zodResolver } from "@hookform/resolvers/zod"
import { Pressable, View } from "react-native"
import { Controller, useForm } from "react-hook-form"

import { ModalWrapper, Text, TextInput, MCIcon } from "@src/components/core"
import { createExpenseCategoryInput } from "@white-labels/input"
import { Alert } from "@src/components/ui/confirm"
import { ar } from "@src/i18n"
import { trpc } from "@src/utils"

export type ExpenseModalState = { type: "add" } | { type: "edit"; id: number }

export function ExpenseCategoryFormModal({ state, onClose }: { state: ExpenseModalState; onClose: () => void }) {
  const utils = trpc.useUtils()
  const categories = trpc.expenseCategory.list.useQuery()
  const createCategory = trpc.expenseCategory.create.useMutation()
  const editCategory = trpc.expenseCategory.edit.useMutation()

  const category = state.type === "edit" ? categories.data?.items.find((c) => c.id === state.id) : undefined

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(createExpenseCategoryInput),
    defaultValues: { name: category?.name ?? "", color: category?.color ?? undefined },
  })

  const loading = createCategory.isPending || editCategory.isPending
  const disabled = !formState.isValid || !formState.isDirty || loading

  const onSubmit = handleSubmit((data) => {
    const opts = {
      onSuccess: () => {
        utils.expenseCategory.list.invalidate()
        utils.expense.list.invalidate()
        onClose()
      },
      onError: (e: { message: string }) => Alert.alert(ar.error, e.message),
    }
    if (state.type === "edit") {
      editCategory.mutate({ id: state.id, name: data.name, color: data.color }, opts)
    } else {
      createCategory.mutate({ name: data.name, color: data.color }, opts)
    }
  })

  return (
    <ModalWrapper onClose={onClose}>
      <View className="flex-row items-center justify-between px-2 border-b border-gray-100">
        <Text className="text-lg font-extrabold text-gray-900 tracking-tight">{state.type === "edit" ? ar.editExpenseCategory : ar.newExpenseCategory}</Text>
        <Pressable onPress={onClose} className="p-3">
          <MCIcon name="close" size={22} color="#6B7280" />
        </Pressable>
      </View>

      <Text className="text-sm text-gray-600 mb-1">{ar.categoryName}</Text>
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, value } }) => <TextInput placeholder={ar.categoryName} value={value} onChangeText={onChange} autoFocus />}
      />

      <Text className="text-sm text-gray-600 mb-1 mt-3">{ar.color}</Text>
      <Controller
        control={control}
        name="color"
        render={({ field: { onChange, value } }) => (
          <View className="flex-row flex-wrap gap-2">
            {SWATCHES.map((c) => {
              const selected = value === c
              return (
                <Pressable
                  key={c}
                  onPress={() => onChange(c)}
                  className={`w-9 h-9 rounded-full items-center justify-center ${selected ? "border-2 border-gray-900" : "border border-gray-200"}`}
                  style={{ backgroundColor: c }}
                >
                  {selected && <MCIcon name="check" size={16} color="#fff" />}
                </Pressable>
              )
            })}
          </View>
        )}
      />

      <Pressable
        className={`h-10 rounded mt-4 items-center justify-center active:opacity-90 disabled:opacity-50 ${disabled ? "bg-gray-400" : "bg-blue-600"}`}
        onPress={onSubmit}
        disabled={disabled}
      >
        <Text className="text-white font-bold">{loading ? ar.loading : ar.save}</Text>
      </Pressable>
    </ModalWrapper>
  )
}

const SWATCHES = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#0EA5E9",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#D946EF",
  "#EC4899",
  "#64748B",
  "#78716C",
]
